<template>
  <div class="w-full text-sm">
    <div v-if="loading" class="w-40 h-40 items-center justify-center flex">
      <Spinning_Loader/>
    </div>

    <div v-else class="w-full p-4 bg-gray-50 mt-2 rounded-lg border border-gray-200 shadow-sm">
      <!-- Patient Information -->
      <div class="mb-4 pb-4 border-b border-gray-200">
        <div class="space-y-2">
          <div class="flex items-center">
            <span class="font-semibold text-gray-700 mr-2">Patient:</span>
            <span class="sensitive text-gray-900">{{ task.metadata.bene_first_name }} {{ task.metadata.bene_last_name }}</span>
          </div>
          <div class="flex items-center">
            <span class="font-semibold text-gray-700 mr-2">Patient ID:</span>
            <span class="sensitive text-gray-900">{{ task.metadata.patient_identifier }}</span>
          </div>
          <div v-if="task.metadata.bene_dob" class="flex items-center">
            <span class="font-semibold text-gray-700 mr-2">DOB:</span>
            <span class="sensitive text-gray-900">{{ task.metadata.bene_dob?.split(' ')[0] }}</span>
          </div>
          <div v-if="task.metadata.rendering_npis && task.metadata.rendering_npis.length > 0" class="flex items-start">
            <span class="font-semibold text-gray-700 mr-2">NPIs:</span>
            <div class="flex flex-wrap gap-1.5">
              <span v-for="npi in task.metadata.rendering_npis" :key="npi"
                    class="sensitive inline-block rounded-md border border-primary-300 px-2 py-0.5 bg-primary-100 text-primary-800 font-medium">
                {{ npi }}
              </span>
            </div>
          </div>
          <div v-if="task.metadata.tin" class="flex items-center">
            <span class="font-semibold text-gray-700 mr-2">TIN:</span>
            <span class="text-gray-900 sensitive">{{ task.metadata.tin }}</span>
          </div>
        </div>
      </div>

      <!-- Current Year HCCs -->
      <div class="mb-4">
        <div class="font-semibold text-gray-900 mb-2">All Patient HCCs Billed This Performance Year:</div>
        <div class="text-xs text-gray-600 mb-3">(Blue indicates billed by patient PCP, hover over for more information)</div>

        <div v-if="hasCurrentHCCs" class="space-y-2.5">
          <div v-for="(value, key) in currentHCCs" :key="key" v-show="value && value.length > 0">
            <div class="flex items-center gap-2">
              <span class="font-semibold text-gray-700 min-w-[2rem]">v{{ key }}:</span>
              <div class="flex flex-wrap items-center gap-1.5">
                <div v-for="hcc in [...value].sort((a, b) => a.code - b.code)" :key="hcc.code"
                     class="rounded-md border px-2 py-1 cursor-help font-semibold"
                     :class="{'bg-blue-100 border-blue-400 text-blue-900': hcc.billed_by_practice, 'bg-amber-100 border-amber-400 text-amber-900': !hcc.billed_by_practice}"
                     :title="hccTitle(hcc)">
                  {{ hcc.code }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-sm text-gray-500 italic">No codes billed this year</div>
      </div>

      <!-- Previous Year HCCs -->
      <div>
        <div class="font-semibold text-gray-900 mb-2">Codes Captured Last Year:</div>
        <div class="text-xs text-gray-600 mb-3">(Blue indicates billed by patient PCP, hover over for more information)</div>

        <div v-if="hasRecaptureHCCs" class="space-y-2.5">
          <div v-for="(value, key) in recaptureHCCs" :key="key" v-show="value && value.length > 0">
            <div class="flex items-center gap-2">
              <span class="font-semibold text-gray-700 min-w-[2rem]">v{{ key }}:</span>
              <div class="flex flex-wrap items-center gap-1.5">
                <div v-for="hcc in [...value].sort((a, b) => a.code - b.code)" :key="hcc.code"
                     class="rounded-md border px-2 py-1 cursor-help font-semibold"
                     :class="{'bg-blue-100 border-blue-400 text-blue-900': hcc.billed_by_practice, 'bg-amber-100 border-amber-400 text-amber-900': !hcc.billed_by_practice}"
                     :title="hccTitle(hcc)">
                  {{ hcc.code }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-sm text-gray-500 italic">No codes captured last year</div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Spinning_Loader from '@/components/widgetcomponents/Viz_Elements/Spinning_Loader.vue';
import { isDemoMode, spoofText } from '@/utils/demoSpoof';

export default {
  components: {
    Spinning_Loader
  },
  props: {
    task: {
      type: Object,
      required: true
    }
  },
  emits: ['can_submit'],
  data() {
    return {
      currentHCCs: {
        24: [],
        28: []
      },
      recaptureHCCs: {
        24: [],
        28: []
      },
      loading: false
    }
  },
  computed: {
    hasCurrentHCCs() {
      return Object.values(this.currentHCCs).some(arr => arr && arr.length > 0);
    },
    hasRecaptureHCCs() {
      return Object.values(this.recaptureHCCs).some(arr => arr && arr.length > 0);
    }
  },
  methods: {
    // Hover title for an HCC chip. The chip itself shows the HCC code, which must
    // stay readable, so it cannot carry `.sensitive`; spoof the billing NPI/TIN
    // here instead (spoofText fails closed to '' for values it cannot mask).
    hccTitle(hcc) {
      const demo = isDemoMode();
      const npi = demo && hcc.npi ? spoofText(String(hcc.npi)) : hcc.npi;
      const tin = demo && hcc.tin ? spoofText(String(hcc.tin)) : hcc.tin;
      return `Claim Date: ${hcc.clm_thru_dt} | NPI: ${npi} | TIN: ${tin} | Billed By Practice: ${hcc.billed_by_practice ? 'Yes' : 'No'} | Billed DX: ${hcc.billed_dx.join(', ')}`;
    },
    async fetchHCCs() {
      if (!this.task.metadata?.patient_identifier) {
        console.error('Error fetching HCC data: Missing patient identifier');
        return;
      }

      this.loading = true;

      try {
        const response = await axios.post('api/returnmetricinsight/', {
          metric_id: 34,
          metric_insight_id: 1,
          insight_type: 'task_insight',
          sql_filters: {
            observation_dt_year: { 'IN': [new Date().getFullYear(), new Date().getFullYear() - 1] },
            bene_mbi_id: this.task.metadata.patient_identifier
          }
        });

        if (response?.data?.length > 0) {
          const currentYear = new Date().getFullYear();

          this.currentHCCs = { 24: [], 28: [] };
          this.recaptureHCCs = { 24: [], 28: [] };

          response.data
            .filter(item => item.model_num !== 22)
            .forEach(item => {
              const { model_num, hcc_code, billed_by_practice, observation_dt_year } = item;
              const hccData = {
                code: hcc_code,
                billed_by_practice,
                clm_thru_dt: item.clm_thru_dt,
                billed_dx: JSON.parse(item.billed_dx || '[]'),
                npi: item.npi,
                tin: item.tin
              };

              const targetCollection = observation_dt_year === currentYear
                ? this.currentHCCs
                : this.recaptureHCCs;

              if (!targetCollection[model_num]) {
                targetCollection[model_num] = [];
              }

              const existingIndex = targetCollection[model_num].findIndex(hcc => hcc.code === hcc_code);

              if (existingIndex === -1) {
                targetCollection[model_num].push(hccData);
              } else if (!targetCollection[model_num][existingIndex].billed_by_practice && billed_by_practice) {
                targetCollection[model_num][existingIndex] = hccData;
              }
            });
        }
      } catch (error) {
        console.error('Error fetching HCC data:', error);
      } finally {
        this.loading = false;
      }
    }
  },
  mounted() {
    this.fetchHCCs();
    this.$emit('can_submit', true, '');
  }
}
</script>
